import { createSlice } from '@reduxjs/toolkit';
import { SustainabilityFormDto } from '../../models/sustainability';
import sustainabilityApi from '../apis/sustainability.api';

const initialState: {
	formsByProfileId: Record<number, SustainabilityFormDto | null>;
} = {
	formsByProfileId: {}
};

const updateSustainabilityFormInternal = (
	state: { formsByProfileId: Record<number, SustainabilityFormDto | null> },
	profileId: number,
	form: SustainabilityFormDto | null
) => {
	state.formsByProfileId[profileId] = form;
};

const sustainabilitySlice = createSlice({
	name: 'sustainability',
	initialState,
	reducers: {
		clearSustainabilityForm(state, action: { payload?: number }) {
			const profileId = action.payload;
			if (profileId === undefined) {
				state.formsByProfileId = {};
			} else {
				delete state.formsByProfileId[profileId];
			}
		},
		setSustainabilityForm(
			state,
			action: { payload: { profileId: number; form: SustainabilityFormDto | null } }
		) {
			const { profileId, form } = action.payload;
			updateSustainabilityFormInternal(state, profileId, form);
		}
	},
	extraReducers: (builder) => {
		const handleFulfilled = (
			state: any,
			form: SustainabilityFormDto | null,
			profileId?: number
		) => {
			const id = form?.profileId ?? profileId;
			// eslint-disable-next-line eqeqeq
			if (id != null) {
				updateSustainabilityFormInternal(state, id, form);
			}
		};

		builder
			.addMatcher(sustainabilityApi.endpoints.getForm.matchFulfilled, (state, action) => {
				handleFulfilled(state, action.payload, action.meta.arg?.originalArgs.profileId);
			})
			.addMatcher(sustainabilityApi.endpoints.createForm.matchFulfilled, (state, action) => {
				handleFulfilled(state, action.payload, action.meta.arg?.originalArgs.profileId);
			})
			.addMatcher(sustainabilityApi.endpoints.updateForm.matchFulfilled, (state, action) => {
				handleFulfilled(state, action.payload, action.meta.arg?.originalArgs.profileId);
			})
			.addMatcher(
				sustainabilityApi.endpoints.getLatestApprovedForm.matchFulfilled,
				(state, action) =>
					handleFulfilled(state, action.payload ?? null, action.meta.arg?.originalArgs)
			)
			.addMatcher(
				sustainabilityApi.endpoints.getLatestInProgressForm.matchFulfilled,
				(state, action) =>
					handleFulfilled(state, action.payload ?? null, action.meta.arg.originalArgs)
			)
			.addMatcher(
				sustainabilityApi.endpoints.getLatestDeclinedForm.matchFulfilled,
				(state, action) =>
					handleFulfilled(state, action.payload ?? null, action.meta.arg.originalArgs)
			);
	}
});

export const { clearSustainabilityForm, setSustainabilityForm } = sustainabilitySlice.actions;

export const selectFormByProfileId = (
	state: { sustainability: { formsByProfileId: Record<number, SustainabilityFormDto | null> } },
	profileId: number
): SustainabilityFormDto | null => state.sustainability?.formsByProfileId?.[profileId] ?? null;

export default sustainabilitySlice.reducer;
